import type { Constructor, Container } from '@packages/core/container';

import { type PatchCaseUpdateBody, StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildCreateCaseBody } from '../helpers';

/** Parameters for creating a techline case. */
export type CreateCaseParams = {
    /** The retailer number associated with the case. */
    retailerNumber: string;

    /** The username making the update. */
    username: string;

    /** The VIN associated with the case. */
    vin: string;
};

/**
 * Create a new Techline case in draft status.
 */
export class CreateCase extends StsBackendOperation {
    /**
     * Register this operation with additional dependency on AnalyticsService.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static `this` in an abstract class is not assignable to Constructor without a cast
        container.register(this as any as Constructor, [StsBackendClient, AnalyticsService]);
    }

    /** @param analyticsService - the analytics service for logging events */
    private analyticsService: AnalyticsService;

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(client: StsBackendClient, analyticsService: AnalyticsService) {
        super(client);
        this.analyticsService = analyticsService;
    }
    /**
     * Build the creation payload and persist the case via the API.
     *
     * @param params - the case data and identity fields
     * @param signal - optional abort signal for request cancellation
     * @returns the case number if the creation succeeded, undefined otherwise
     */
    async exec(params: CreateCaseParams, signal?: AbortSignal): Promise<string | undefined> {
        const updateBody = buildCreateCaseBody(params) as PatchCaseUpdateBody;
        const response = await this.client.updateCase({ updateBody }, signal);

        if (response?.success) {
            this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_CREATES_DRAFT_TECHLINE_CASE);
            return response.data?.results?.caseNumber;
        }

        return undefined;
    }
}
